/* global BigInt */
// Reversible, dependency-free obfuscation of numeric character IDs for URLs.
//
// This is NOT encryption — it just keeps raw, sequential database IDs out of
// shareable links (e.g. /character/qkuizj instead of /character/123456).
//
// It works by multiplying the ID by an odd constant within a fixed 32-bit ring.
// Multiplying by an odd number (and later by its modular inverse) is a perfect
// 1-to-1 mapping over the ring, so every ID maps to exactly one scrambled value
// and back. The result is base36-encoded for a compact, URL-safe string.

const MOD = 1n << 32n;       // 32-bit ring → supports IDs up to ~4.29 billion
const MASK = MOD - 1n;
const MULT = 2654435761n;    // odd constant (Knuth's multiplicative hash)
const SALT = 0x5f3759dfn;    // XOR offset so id 0 doesn't map to a trivial slug

// Modular inverse of MULT mod 2^32 via the extended Euclidean algorithm.
// Computed once at load so we never have to hand-maintain a magic constant.
function modInverse(a, m) {
    let [oldR, r] = [a % m, m];
    let [oldS, s] = [1n, 0n];
    while (r !== 0n) {
        const q = oldR / r;
        [oldR, r] = [r, oldR - q * r];
        [oldS, s] = [s, oldS - q * s];
    }
    return ((oldS % m) + m) % m;
}

const INV = modInverse(MULT, MOD);

/**
 * Encode a numeric character ID into an opaque, URL-safe slug.
 * @param {number|string} id
 * @returns {string|null} base36 slug, or null if the id is out of range
 */
export function encodeCharacterId(id) {
    if (id == null || id === '') return null;
    const n = BigInt(id);
    if (n < 0n || n >= MOD) return null;
    const scrambled = ((n * MULT) & MASK) ^ SALT;
    return scrambled.toString(36);
}

/**
 * Decode a slug back into the original numeric character ID.
 * @param {string} slug
 * @returns {number|null} the character ID, or null if the slug is malformed
 */
export function decodeCharacterId(slug) {
    if (!slug || !/^[0-9a-z]+$/i.test(slug)) return null;
    const scrambled = parseInt(slug, 36);
    if (!Number.isFinite(scrambled)) return null;
    const unsalted = (BigInt(scrambled) ^ SALT) & MASK;
    return Number((unsalted * INV) & MASK);
}
